import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import DynamicFormFields from '../components/DynamicFormFields';
import { sanitizeInput, isValidEmail } from '../utils/sanitization';
import { Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Register = () => {
  const [branding, setBranding] = useState({ siteIcon: '🐕', tagline: 'Create your account', logoBackgroundColor: '#2563eb' });
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/business-info/site_branding`)
      .then(res => {
        if (res.data.info_value) {
          const d = JSON.parse(res.data.info_value);
          setBranding({
            siteIcon: d.siteIcon || '🐕',
            tagline: d.tagline || 'Create your account',
            logoBackgroundColor: d.logoBackgroundColor || '#2563eb',
          });
        }
      })
      .catch(() => {});
  }, []);

  const handleChange = (newData) => {
    // Sanitize all text fields in the form data
    const sanitized = {};
    for (const [key, value] of Object.entries(newData)) {
      if (typeof value === 'string' && key !== 'password' && key !== 'confirmPassword') {
        sanitized[key] = sanitizeInput(value, {
          allowEmojis: false,
          allowHtml: false,
          maxLength: key === 'email' ? 255 : 1000,
          allowSpecialChars: true
        });
      } else {
        sanitized[key] = value;
      }
    }
    setFormData(sanitized);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email if present
    if (formData.email && !isValidEmail(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (formData.password.length > 100) {
      toast.error('Password is too long');
      return;
    }

    setLoading(true);

    try {
      // Send all form data including custom fields
      await register({
        ...formData,
        firstName: formData.first_name,
        lastName: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
      });
      toast.success('Registration successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: branding.logoBackgroundColor }}>
            <span className="text-white text-3xl">{branding.siteIcon}</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Create Account</h2>
          <p className="mt-2 text-gray-600">{branding.tagline}</p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Dynamic form fields from database */}
            <DynamicFormFields
              formData={formData}
              onChange={handleChange}
            />

            <div>
              <label htmlFor="password" className="label">
                Password <span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="input pr-10"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="At least 6 characters"
                />
                <button
                  type="button"
                  onMouseDown={() => setShowPassword(true)}
                  onMouseUp={() => setShowPassword(false)}
                  onMouseLeave={() => setShowPassword(false)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 select-none"
                  tabIndex={-1}
                  title="Hold to reveal password"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                Confirm Password <span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  className="input pr-10"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                />
                <button
                  type="button"
                  onMouseDown={() => setShowConfirmPassword(true)}
                  onMouseUp={() => setShowConfirmPassword(false)}
                  onMouseLeave={() => setShowConfirmPassword(false)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 select-none"
                  tabIndex={-1}
                  title="Hold to reveal password"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn btn-primary mt-6"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
